// @flow
import { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import Button, { buttonConstants } from '@shared/Button';
import ParticipantIndicator from '@shared/ParticipantIndicator';
import { InterventionRegistration, InterventionRegistrationType, InterventionType } from '@root/src/Intervention/types';
import { PERSON_TYPE_BENEFICIARY } from '@person/constants';
import {
  INTERVENTION_REGISTRATION_TYPE_ABSENT,
  INTERVENTION_REGISTRATION_TYPE_EXCUSED,
  INTERVENTION_REGISTRATION_TYPE_PRESENT,
  INTERVENTION_REGISTRATION_TYPE_SCHEDULED,
} from '@intervention/constants';
import { INTERVENTION_REGISTRATION_STATUSES } from '@root/src/Intervention/constants';
import { uppercaseFirst } from '@common/services/CommonUtils';

type InterventionBeneficiariesProps = {
  intervention: InterventionType,
  registrations: InterventionRegistration[],
  onChangeStatus: (personId: number, status: InterventionRegistrationType) => void,
  onMarkAllPresent: () => void,
  onCancel: () => void,
  onSave: () => void,
  isSaving: boolean,
  hasUnsavedChanges: boolean,
};

const InterventionRegistrations = (props: InterventionBeneficiariesProps): React$Node => {
  const {
    intervention,
    registrations,
    onChangeStatus,
    onMarkAllPresent,
    onCancel,
    onSave,
    isSaving,
    hasUnsavedChanges,
  } = props;

  const { name } = intervention;

  const { t } = useTranslation();

  const statusLabels: { [InterventionRegistrationType]: string } = useMemo(() => ({
    [INTERVENTION_REGISTRATION_TYPE_SCHEDULED]: t('groupClasses.groupClass.interventions.registrationStatus.scheduled'),
    [INTERVENTION_REGISTRATION_TYPE_PRESENT]: t('groupClasses.groupClass.interventions.registrationStatus.present'),
    [INTERVENTION_REGISTRATION_TYPE_ABSENT]: t('groupClasses.groupClass.interventions.registrationStatus.absent'),
    [INTERVENTION_REGISTRATION_TYPE_EXCUSED]: t('groupClasses.groupClass.interventions.registrationStatus.excused'),
  }), [t]);

  const handleMarkAll = (): void => {
    onMarkAllPresent();
  };

  const handleStatus = (personId: number, status: InterventionRegistrationType): void => {
    onChangeStatus(personId, status);
  };

  return (
    <div className="interventions-participants__card">
      <div className="interventions-participants__header">
        <div>
          <p className="interventions-participants__title">{ name }</p>
          <p className="interventions-participants__subtitle">{ `${ registrations.length || 0 } ${ t('user.participants').toLowerCase() }` }</p>
        </div>
        <div className="interventions-participants__header-actions">
          <Button
            type={ buttonConstants.PRIMARY }
            className="interventions-participants__mark-all"
            onClick={ handleMarkAll }
            disabled={ isSaving || registrations.length <= 0 }
          >
            { t('groupClasses.groupClass.interventions.actions.markAllPresent') }
          </Button>
        </div>
      </div>

      <div className="interventions-participants__list">
        { registrations.length > 0 ? (
          registrations.map((registration) => {
            const { firstName, lastName } = registration;
            const personId = registration.beneficiaryId ?? registration.participantId;
            const isShapeParticipant = registration.isShapeParticipant === true;
            const isBeneficiary = !isShapeParticipant && (registration.personType
              ? registration.personType === PERSON_TYPE_BENEFICIARY
              : registration.beneficiaryId !== null && registration.beneficiaryId !== undefined);
            const isParticipant = !isShapeParticipant && !isBeneficiary;
            const rowClassName = clsx('interventions-participants__row', {
              'interventions-participants__row--shape': isShapeParticipant,
              'interventions-participants__row--beneficiary': isBeneficiary,
              'interventions-participants__row--participant': isParticipant,
            });
            const initials = `${ firstName?.[0] || '' }${ lastName?.[0] || '' }`.toUpperCase();
            const fullName = `${ uppercaseFirst(firstName) } ${ uppercaseFirst(lastName) }`;

            return (
              <div key={ String(personId) } className={ rowClassName }>
                <div className="interventions-participants__identity">
                  <ParticipantIndicator
                    isBeneficiary={ isBeneficiary }
                    isShapeUser={ isShapeParticipant }
                    extraCss="participant-tab"
                  />
                  <div className="interventions-participants__avatar">{ initials }</div>
                  <div>
                    <p className="interventions-participants__name">{ fullName }</p>
                  </div>
                </div>
                <div className="interventions-participants__statuses">
                  { INTERVENTION_REGISTRATION_STATUSES.map((status: InterventionRegistrationType) => {
                    const isActive = registration.status === status;
                    return (
                      <button
                        key={ status }
                        type="button"
                        className={ `interventions-participants__status ${ isActive ? 'interventions-participants__status--active' : '' } interventions-participants__status--${ status }` }
                        onClick={ () => handleStatus(personId, status) }
                      >
                        { statusLabels[status] }
                      </button>
                    );
                  }) }
                </div>
              </div>
            );
          })
        ) : (
          <div className="interventions-participants__empty">
            <p>
              { t('groupClasses.groupClass.interventions.beneficiaries.empty') }
            </p>
          </div>
        ) }
      </div>

      <div className="interventions-participants__footer">
        <Button
          type={ buttonConstants.TERTIARY }
          className="interventions-participants__action"
          onClick={ onCancel }
          disabled={ isSaving || !hasUnsavedChanges }
          isOutlined
        >
          { t('common.cancel') }
        </Button>
        <Button
          type={ buttonConstants.SECONDARY }
          className="interventions-participants__action"
          onClick={ onSave }
          disabled={ isSaving || !hasUnsavedChanges }
          isLoading={ isSaving }
        >
          { t('groupClasses.groupClass.interventions.actions.saveChanges') }
        </Button>
      </div>
    </div>
  );
};

export default InterventionRegistrations;
